import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import { TicketKey } from 'flow/types/EntityKeys';
import useAppContext from 'flow/hooks/useAppContext';

import {
    selectApiHasMoreRelatedTicketsData,
    selectTicketsRelatedSortingData,
    selectTicketsSortField,
    selectTicketsSortDirection,
} from 'flow/selectors/TicketsSelectors';

import { makeTicketsKeysDataComparator } from 'flow/utils/ticket-utils';

import {
    fetchRelatedTicketsLatest,
    fetchRelatedTicketsNext,
} from 'flow/actions/tickets-action';

import EmptyBubble from 'flow/components/nav/empty-states/EmptyBubble';
import TicketsList from './TicketsList';
import { Seq } from 'immutable';

const KEY_SEQ_EMPTY: Seq.Indexed<TicketKey> = Seq.Indexed();

interface Props {
    ticketKey: TicketKey;
    onClick: (key: TicketKey) => void;
    scrollableTargetId: string;
}
const TicketsListRelatedWrapper = ({
    onClick,
    ticketKey,
    scrollableTargetId,
}: Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();

    const ticketKeysData = useAppSelector((state) =>
        selectTicketsRelatedSortingData(state, ticketKey),
    );
    const sortField = useAppSelector(selectTicketsSortField);
    const sortDirection = useAppSelector(selectTicketsSortDirection);

    const ticketKeys = useMemo(() => {
        if (!ticketKeysData) return KEY_SEQ_EMPTY;
        return ticketKeysData
            .sort(makeTicketsKeysDataComparator(sortField, sortDirection))
            .keySeq();
    }, [ticketKeysData, sortField, sortDirection]);

    const apiHasMoreData = useAppSelector((state) =>
        selectApiHasMoreRelatedTicketsData(state, ticketKey),
    );

    const [isFetchingLatestData, setIsFetchingLatestData] = useState(false);

    useEffect(() => {
        setIsFetchingLatestData(true);
        dispatch(fetchRelatedTicketsLatest(ticketKey)).finally(() => {
            setIsFetchingLatestData(false);
        });
    }, [dispatch, ticketKey]);

    const fetchNext = useCallback(() => {
        dispatch(fetchRelatedTicketsNext(ticketKey));
    }, [dispatch, ticketKey]);

    return ticketKeys.isEmpty() && !isFetchingLatestData ? (
        <EmptyBubble
            title={i18next.t('tickets.related.none')}
            imageId={'supervisorCircle'}
        />
    ) : (
        <TicketsList
            key={'TICKET_RELATED'}
            itemKeys={ticketKeys}
            handleListItemClicked={onClick}
            displayLoadingItem={isFetchingLatestData && ticketKeys.isEmpty()}
            onReachedEndOfList={fetchNext}
            canFetchMoreData={apiHasMoreData}
            scrollableTargetId={scrollableTargetId}
        />
    );
};

export default React.memo(TicketsListRelatedWrapper);
